import { ClientDelegate } from "./client.delegate";
import
{
    IClient,
    IClientAddress,
    INewClient,
    INewClientAddress,
    INewClientContractDocument,
    IClientResponse,
    IBaseResponse,
    IClientContact,
    IClientOtherContact,
    INewClientBulk
} from "./client.interface";
import { Observable, BehaviorSubject, of as observableOf, of } from 'rxjs';
import { mergeMap, catchError, share, filter } from "rxjs/operators";
import { addProvider, IProviderDescriptor } from "../ServiceUtil";
import { AuthenticationService } from "../AuthenticationService";

export class ClientService
{
    private clientsData: IClient[] = null;
    private clientsSubject: BehaviorSubject<IClient[]> = new BehaviorSubject(null);
    public clients: Observable<IClient[]> = this.clientsSubject.pipe(filter((data) => !!data));

    private clientsFilterSubject: BehaviorSubject<IClient[]> = new BehaviorSubject(null);
    public clientsFiltered: Observable<IClient[]> = this.clientsFilterSubject.pipe(filter((data) => !!data));

    private selectedClientId: string = '0';

    private selectedClientData: IClient = null;
    private selectedClientSubject: BehaviorSubject<IClient> = new BehaviorSubject(this.selectedClientData);
    public selectedClient: Observable<IClient> = this.selectedClientSubject.pipe(filter((data) => !!data));

    constructor(private clientDelegate: ClientDelegate, private authenticationService: AuthenticationService)
    {

    }

    public get clientId(): string
    {
        return this.selectedClientId;
    }

    public getSelectedClientById(clientId: string): Observable<IBaseResponse>
    {
        if (clientId === "0")
        {
            this.selectedClientData = null;
            this.selectedClientSubject.next(this.selectedClientData);
            return observableOf({
                success: false,
                errorString: "Not Found"
            });
        }
        var response = this.clientDelegate.getAllClientById(clientId);

        return this.processClient(response);
    }

    public setSelectedClientId(clientId: string): void
    {
        this.selectedClientId = clientId;
    }

    public getAllClients(): Observable<IBaseResponse>
    {
        if (this.clientsData !== null && this.clientsData.length > 0)
        {
            return observableOf({
                success: true
            });
        }
        const response = this.clientDelegate.getAllClients();
        return this.processClients(response);
    }

    public createNewClient(client: INewClient): Observable<IBaseResponse>
    {
        const response = this.clientDelegate.createNewClient({ ...client, ...this.authenticationService.getBaseRequest });
        return this.processClients(response);
    }

    public createNewAddress(clientContact: IClientAddress, clientId: string): Observable<IBaseResponse>
    {
        const newAddress: INewClientAddress = {
            ...clientContact,
            clientId: clientId,
            ...this.authenticationService.getBaseRequest
        };
        const response = this.clientDelegate.createClientAddress(newAddress);

        return this.processClient(response);
    }

    public createNewContact(clientContact: IClientContact, clientId: string): Observable<IBaseResponse>
    {
        const newAddress: IClientContact = {
            ...clientContact,
            clientId: clientId,
            ...this.authenticationService.getBaseRequest
        };
        const response = this.clientDelegate.createClientContact(newAddress);

        return this.processClient(response);
    }

    public createNewClientsBulk(benchLead: INewClientBulk): Observable<IBaseResponse>
    {
        const response = this.clientDelegate.createNewClientBulk({
            ...benchLead,
            ...this.authenticationService.getBaseRequest
        });
        return this.processClients(response);
    }


    public createNewOtherContact(clientContact: IClientOtherContact, clientId: string): Observable<IBaseResponse>
    {
        const newAddress: IClientOtherContact = {
            ...clientContact,
            clientId: clientId,
            ...this.authenticationService.getBaseRequest
        };
        const response = this.clientDelegate.createClientOtherContact(newAddress);

        return this.processClient(response);
    }

    public createNewContractDocuments(clientContractDocuments: INewClientContractDocument): Observable<IBaseResponse>
    {
        const response = this.clientDelegate.createClientContracts({ ...clientContractDocuments, ...this.authenticationService.getBaseRequest });
        return this.processClient(response);
    }

    public updateClient(client: IClient): Observable<IBaseResponse>
    {
        const response = this.clientDelegate.updateClient({ ...client, ...this.authenticationService.getBaseRequest });
        return this.processClient(response);
    }

    public updateAddress(clientContact: IClientAddress, clientId: string): Observable<IBaseResponse>
    {
        clientContact.clientId = clientId;
        const response = this.clientDelegate.updateClientAddress({ ...clientContact, ...this.authenticationService.getBaseRequest });
        return this.processClient(response);
    }

    public updateClientContact(clientContact: IClientContact, clientId: string): Observable<IBaseResponse>
    {
        clientContact.clientId = clientId;
        const response = this.clientDelegate.updateClientContact({ ...clientContact, ...this.authenticationService.getBaseRequest });
        return this.processClient(response);
    }

    public updateClientOtherContact(clientContact: IClientOtherContact, clientId: string): Observable<IBaseResponse>
    {
        clientContact.clientId = clientId;
        const response = this.clientDelegate.updateClientOtherContact({ ...clientContact, ...this.authenticationService.getBaseRequest });
        return this.processClient(response);
    }

    public filterClients(searchTerm: string, filterBy: string)
    {
        let data = this.clientsData;
        if (filterBy == null)
        {
            data = this.clientsData.filter(x =>
                x.clientName.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
                || x.clientCode.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
            );
        }
        else 
        {
            data = this.clientsData.filter(x =>
                x[filterBy].trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
            );
        }

        this.clientsFilterSubject.next(data);
    }

    private processClients(clients: Observable<IClientResponse>): Observable<IBaseResponse>
    {
        clients.subscribe(onClientSuccess.bind(this), onClientsFault.bind(this));

        return clients.pipe(mergeMap((response: IClientResponse) => observableOf({
            success: response.success,
            errorString: response.errorString

        })),
            catchError((response: IClientResponse) =>
            {
                return of({
                    success: response.success,
                    errorString: response.errorString
                });
            }),
            share());

        function onClientSuccess(clientResponse: IClientResponse)
        {
            if (clientResponse.success)
            {
                this.selectedClientId = clientResponse.clientId ? clientResponse.clientId : this.selectedClientId;
                this.clientsData = clientResponse.clients;
                this.clientsSubject.next(this.clientsData);
                this.clientsFilterSubject.next(this.clientsData);

                this.selectedClientData = this.clientsData.find(x => x.clientId === this.selectedClientId);
                this.selectedClientSubject.next(this.selectedClientData);

            }
        }

        function onClientsFault(error: any)
        {
            //ClientService.logger.error(`processClients Fault - Error (${JSON.stringify(error)})`);
        }
    }

    private processClient(clients: Observable<IClientResponse>): Observable<IBaseResponse>
    {
        clients.subscribe(onClientSuccess.bind(this), onClientsFault.bind(this));

        return clients.pipe(mergeMap((response: IClientResponse) => observableOf({
            success: response.success,
            errorString: response.errorString
        })),
            catchError((response: IClientResponse) => observableOf({
                success: response.success,
                errorString: response.errorString
            })),
            share());

        function onClientSuccess(clientResponse: IClientResponse)
        {
            if (clientResponse.success)
            {
                this.selectedClientId = clientResponse.clientId ? clientResponse.clientId : this.selectedClientId;
                this.selectedClientData = clientResponse.client;
                this.selectedClientSubject.next(this.selectedClientData);
            }
        }

        function onClientsFault(error: any)
        {
            //  ClientService.logger.error(`processClients Fault - Error (${JSON.stringify(error)})`);
        }
    }
}